import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Search, Undo2, Eye, ScrollText, ChevronLeft, ChevronRight } from "lucide-react";
import { DateInput } from "./ui/date-input";

const ALL = "__all__";
const fmtAt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const KIND_LABEL = { action: "Ενέργεια", request: "Ενέργεια", undo: "ΑΝΑΙΡΕΣΗ" };

const EntryChanges = ({ entry }) => {
  const changes = entry?.changes || [];
  if (!changes.length) {
    return <p className="text-sm text-slate-500">Χωρίς καταγεγραμμένες αλλαγές σε πεδία υποθέσεων{entry?.case_doc_before ? " — υπάρχει πλήρες αντίγραφο της υπόθεσης πριν την ενέργεια (επαναφέρεται με ΑΝΑΙΡΕΣΗ)." : "."}</p>;
  }
  return (
    <div className="max-h-[50vh] overflow-auto border rounded" data-testid="audit-entry-changes">
      <table className="w-full text-xs">
        <thead><tr className="bg-slate-100"><th className="p-2 text-left">ΓΑΚ</th><th className="p-2 text-left">Πεδίο</th><th className="p-2 text-left">Από</th><th className="p-2 text-left">Σε</th></tr></thead>
        <tbody>
          {changes.map((c) => Object.entries(c.fields || {}).map(([f, v], i) => (
            <tr key={`${c.case_id}-${f}`} className="border-t">
              <td className="p-2 font-mono">{i === 0 ? c.gak : ""}</td>
              <td className="p-2">{v.label || f}</td>
              <td className="p-2 text-slate-500">{v.from ?? <span className="text-slate-300">(κενό)</span>}</td>
              <td className="p-2 text-emerald-700">{v.to ?? <span className="text-slate-300">(κενό)</span>}</td>
            </tr>
          )))}
        </tbody>
      </table>
    </div>
  );
};

const AuditLogTab = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [userId, setUserId] = useState(ALL);
  const [action, setAction] = useState(ALL);
  const [q, setQ] = useState("");
  const [undoableOnly, setUndoableOnly] = useState(false);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const LIMIT = 50;
  const [detail, setDetail] = useState(null);
  const [undoTarget, setUndoTarget] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const loadMeta = async () => {
    try {
      const [u, a] = await Promise.all([axios.get(`${API}/admin/audit-log/users`), axios.get(`${API}/admin/audit-log/actions`)]);
      setUsers(u.data?.users || []);
      setActions(a.data?.actions || []);
    } catch (_) { /* noop */ }
  };

  const load = async (newSkip = 0) => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: newSkip };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (userId !== ALL) params.user_id = userId;
      if (action !== ALL) params.action = action;
      if (q.trim()) params.q = q.trim();
      if (undoableOnly) params.undoable_only = true;
      const r = await axios.get(`${API}/admin/audit-log`, { params });
      setData(r.data);
      setSkip(newSkip);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης ενεργειών");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadMeta(); load(0); /* eslint-disable-next-line */ }, []);

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/admin/audit-log/${id}`);
      setDetail(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
  };

  const doUndo = async () => {
    if (!undoTarget) return;
    setUndoing(true);
    try {
      const r = await axios.post(`${API}/admin/audit-log/${undoTarget.id}/undo`);
      toast.success(r.data.message, { duration: 10000 });
      if (r.data.warning) toast.warning(r.data.warning, { duration: 12000 });
      setUndoTarget(null); setDetail(null);
      load(skip); loadMeta();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αναίρεσης", { duration: 10000 });
    } finally { setUndoing(false); }
  };

  const labelOf = (e) => e.label || e.action;
  const uniqActions = Object.values(Object.fromEntries(actions.map((a) => [a.label || a.action, a])));

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ScrollText className="w-5 h-5" /> Ενέργειες χρηστών</CardTitle>
          <CardDescription>
            Αναζήτηση όλων των ενεργειών στο σύστημα ανά ημερομηνία, χρήστη και είδος. Ενέργειες που άλλαξαν στοιχεία υποθέσεων
            (ορισμοί, πράξεις, έδρα, σύνθεση, εισαγωγή Δελτίου, επεξεργασία/διαγραφή υπόθεσης) μπορούν να <b>αναιρεθούν</b> — επαναφέρεται η κατάσταση πριν την ενέργεια.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-xs">Από</Label>
              <DateInput value={dateFrom} max={today} onChange={(e) => setDateFrom(e.target.value)} className="h-10 px-2 text-sm border border-slate-200 rounded-md w-40 block" data-testid="audit-date-from" />
            </div>
            <div>
              <Label className="text-xs">Έως</Label>
              <DateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 px-2 text-sm border border-slate-200 rounded-md w-40 block" data-testid="audit-date-to" />
            </div>
            <div className="min-w-[220px]">
              <Label className="text-xs">Χρήστης</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger data-testid="audit-user-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Όλοι οι χρήστες</SelectItem>
                  {users.filter((u) => u.user_id).map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id} data-testid={`audit-user-option-${u.username}`}>{u.user_name} ({u.username}) · {u.actions}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[260px]">
              <Label className="text-xs">Είδος ενέργειας</Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger data-testid="audit-action-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Όλες οι ενέργειες</SelectItem>
                  {uniqActions.map((a) => <SelectItem key={a.label || a.action} value={a.label || a.action}>{a.label || a.action} · {a.count}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Αναζήτηση (ΓΑΚ, δικαστής, κείμενο)</Label>
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(0)} placeholder="π.χ. 13400/2026 ή Καυκουλα"
                className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md" data-testid="audit-search-input" />
            </div>
            <label className="flex items-center gap-2 text-sm h-10 cursor-pointer">
              <Checkbox checked={undoableOnly} onCheckedChange={(v) => setUndoableOnly(!!v)} data-testid="audit-undoable-only" /> Μόνο αναιρέσιμες
            </label>
            <Button onClick={() => load(0)} disabled={loading} data-testid="audit-search-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />} Αναζήτηση
            </Button>
            <Button variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); setUserId(ALL); setAction(ALL); setQ(""); setUndoableOnly(false); setTimeout(() => load(0), 30); }} data-testid="audit-reset-btn">Καθαρισμός</Button>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span data-testid="audit-total">Σύνολο: {data.total} ενέργειες · εμφάνιση {data.items.length ? skip + 1 : 0}–{skip + data.items.length}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" disabled={skip === 0 || loading} onClick={() => load(Math.max(0, skip - LIMIT))} data-testid="audit-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" disabled={skip + LIMIT >= data.total || loading} onClick={() => load(skip + LIMIT)} data-testid="audit-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="overflow-x-auto border rounded">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="bg-slate-100 text-slate-700 text-xs">
                  <th className="p-2 text-left">Ημ/νία & ώρα</th>
                  <th className="p-2 text-left">Χρήστης</th>
                  <th className="p-2 text-left">Ενέργεια</th>
                  <th className="p-2 text-left">Υποθέσεις (ΓΑΚ)</th>
                  <th className="p-2 text-center">Αλλαγές</th>
                  <th className="p-2 text-center">Κατάσταση</th>
                  <th className="p-2 text-center">Ενέργειες</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e) => (
                  <tr key={e.id} className={`border-t hover:bg-slate-50 ${e.kind === "undo" ? "bg-amber-50" : ""}`} data-testid={`audit-row-${e.id}`}>
                    <td className="p-2 font-mono text-xs whitespace-nowrap">{fmtAt(e.at)}</td>
                    <td className="p-2 text-xs"><div className="font-medium">{e.user_name || "—"}</div><div className="text-slate-400">{e.username}{e.role ? ` · ${e.role}` : ""}</div></td>
                    <td className="p-2 text-xs">
                      <div className="font-medium" data-testid={`audit-row-label-${e.id}`}>{labelOf(e)}</div>
                      {e.meta?.message && <div className="text-slate-500">{e.meta.message}</div>}
                      {e.kind === "request" && e.request?.status >= 400 && <Badge variant="outline" className="text-[10px] border-red-300 text-red-700">HTTP {e.request.status}</Badge>}
                    </td>
                    <td className="p-2 text-xs font-mono max-w-[220px] truncate" title={(e.gaks || []).join(", ")}>{(e.gaks || []).slice(0, 4).join(", ")}{(e.gaks || []).length > 4 ? ` +${e.gaks.length - 4}` : ""}</td>
                    <td className="p-2 text-center text-xs">{e.changed_cases || 0}</td>
                    <td className="p-2 text-center text-xs">
                      {e.kind === "undo" ? <Badge className="bg-amber-500 text-white">{KIND_LABEL.undo}</Badge>
                        : e.undone_at ? <Badge variant="outline" className="border-amber-300 text-amber-700">Αναιρέθηκε</Badge>
                          : e.undoable ? <Badge variant="outline" className="border-emerald-300 text-emerald-700">Αναιρέσιμη</Badge>
                            : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-2 text-center whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openDetail(e.id)} data-testid={`audit-detail-${e.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Λεπτομέρειες</Button>
                      {e.undoable && !e.undone_at && (
                        <Button size="sm" variant="outline" className="h-7 px-2 ml-1 border-red-300 text-red-700 hover:bg-red-50" onClick={() => setUndoTarget(e)} data-testid={`audit-undo-${e.id}`}>
                          <Undo2 className="w-3.5 h-3.5 mr-1" /> ΑΝΑΙΡΕΣΗ
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!loading && data.items.length === 0 && <p className="text-center py-8 text-slate-400 text-sm" data-testid="audit-empty">Δεν βρέθηκαν ενέργειες</p>}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle data-testid="audit-detail-title">{detail ? labelOf(detail) : ""}</DialogTitle>
            <DialogDescription>
              {detail && <>{fmtAt(detail.at)} · {detail.user_name} ({detail.username}) · {detail.request?.method} {detail.request?.path}{detail.undone_at ? ` · Αναιρέθηκε ${fmtAt(detail.undone_at)} από ${detail.undone_by}` : ""}</>}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              {detail.meta?.message && <p className="text-sm">{detail.meta.message}</p>}
              <EntryChanges entry={detail} />
              {detail.meta?.body && <details className="text-xs"><summary className="cursor-pointer text-slate-500">Δεδομένα αιτήματος</summary><pre className="bg-slate-50 p-2 rounded overflow-auto max-h-48">{JSON.stringify(detail.meta.body, null, 1)}</pre></details>}
              {detail.undoable && !detail.undone_at && (
                <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => setUndoTarget(detail)} data-testid="audit-detail-undo-btn"><Undo2 className="w-4 h-4 mr-1" /> ΑΝΑΙΡΕΣΗ ενέργειας</Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!undoTarget} onOpenChange={(o) => !o && setUndoTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2"><Undo2 className="w-5 h-5" /> ΑΝΑΙΡΕΣΗ ενέργειας</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">Θα αναιρεθεί: <b>{undoTarget ? labelOf(undoTarget) : ""}</b> ({undoTarget ? fmtAt(undoTarget.at) : ""}, {undoTarget?.user_name}).</span>
              <span className="block">Επαναφέρεται η κατάσταση <b>{undoTarget?.changed_cases || (undoTarget?.case_ids || []).length}</b> υπόθεσης/εων όπως ήταν ΠΡΙΝ την ενέργεια.
                Αν έγιναν νεότερες αλλαγές στις ίδιες υποθέσεις, θα χαθούν για τα πεδία που επαναφέρονται.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undoing} data-testid="audit-undo-cancel">Άκυρο</AlertDialogCancel>
            <AlertDialogAction onClick={doUndo} disabled={undoing} className="bg-red-600 hover:bg-red-700" data-testid="audit-undo-confirm">
              {undoing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1" />} Ναι, ΑΝΑΙΡΕΣΗ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AuditLogTab;
